import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useDarkMode } from '../contexts/DarkModeContext';
import { useLocalization } from '../hooks/useLocalization';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AboutPage = () => {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { isDarkMode: darkMode } = useDarkMode();
  const navigate = useNavigate();
  const { t } = useLocalization();

  useEffect(() => {
    fetchAboutContent();
  }, []);

  const fetchAboutContent = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(`${API}/about`);
      setContent(response.data.content || '');
    } catch (err) {
      console.error('Error fetching about content:', err);
      setError(t('errorLoadingAbout'));
      // Set fallback content
      setContent(t('fallbackContact'));
    } finally {
      setLoading(false);
    }
  };

  const renderMarkdown = (markdown) => {
    if (!markdown) return '';

    let html = markdown
      // Headers
      .replace(/^### (.*$)/gim, '<h3 class="text-xl font-semibold mb-3 mt-6 text-purple-600">$1</h3>')
      .replace(/^## (.*$)/gim, '<h2 class="text-2xl font-bold mb-4 mt-8 text-purple-700">$1</h2>')
      .replace(/^# (.*$)/gim, '<h1 class="text-3xl font-bold mb-6 mt-10 text-purple-800">$1</h1>')
      
      // Bold and italic
      .replace(/\*\*(.*)\*\*/gim, '<strong class="font-bold text-purple-700">$1</strong>')
      .replace(/\*(.*)\*/gim, '<em class="italic">$1</em>')
      
      // Links - handle both [text](url) and [text] (url) formats
      .replace(/\[([^\]]+)\]\s*\(([^)]+)\)/gim, `<a href="$2" target="_blank" rel="noopener noreferrer" class="text-purple-600 hover:text-purple-800 underline transition-colors duration-200">$1</a>`)
      
      // Images
      .replace(/!\[([^\]]*)\]\(([^)]+)\)/gim, '<img src="$2" alt="$1" class="max-w-full h-auto rounded-lg shadow-md my-6 mx-auto" />')
      
      // Code blocks (simple)
      .replace(/```([^`]+)```/gim, `<pre class="${darkMode ? 'bg-gray-800' : 'bg-gray-100'} p-4 rounded-lg overflow-x-auto my-4"><code>$1</code></pre>`)
      .replace(/`([^`]+)`/gim, `<code class="${darkMode ? 'bg-gray-800' : 'bg-gray-100'} px-2 py-1 rounded text-sm">$1</code>`)
      
      // Lists
      .replace(/^\* (.+$)/gim, '<li class="ml-6 mb-2">• $1</li>')
      .replace(/^- (.+$)/gim, '<li class="ml-6 mb-2">• $1</li>')
      .replace(/^\d+\. (.+$)/gim, '<li class="ml-6 mb-2">$1</li>')
      
      // Line breaks
      .replace(/\n\n/gim, '</p><p class="mb-4 leading-relaxed">')
      .replace(/\n/gim, '<br />');

    // Wrap in paragraph tags
    html = '<p class="mb-4 leading-relaxed">' + html + '</p>';

    // Clean up empty paragraphs
    html = html.replace(/<p class="mb-4 leading-relaxed"><\/p>/gim, '');

    return html;
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      darkMode ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-900'
    }`}>
      {/* Header */}
      <header className={`${
        darkMode ? 'bg-purple-800' : 'bg-purple-600'
      } text-white py-8 transition-colors duration-300`}>
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/')}
                className={`flex items-center text-white hover:text-purple-200 transition-colors duration-300 ${
                  darkMode ? 'hover:text-purple-300' : 'hover:text-purple-200'
                }`}
              >
                <ArrowLeft size={20} className="mr-2" />
                {t('backToHome')}
              </button>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold">{t('aboutPageTitle')}</h1>
            <div className="w-32"></div> {/* Spacer for centering */}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <div className={`max-w-4xl mx-auto ${
          darkMode ? 'bg-gray-800' : 'bg-white'
        } rounded-lg shadow-lg transition-colors duration-300`}>
          <div className="p-8">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className={`animate-spin rounded-full h-12 w-12 border-b-2 ${
                  darkMode ? 'border-purple-400' : 'border-purple-600'
                }`}></div>
                <span className="ml-4 text-lg">{t('loadingContent')}</span>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <div className={`text-lg mb-4 ${
                  darkMode ? 'text-red-400' : 'text-red-600'
                }`}>
                  {error}
                </div>
                <button
                  onClick={fetchAboutContent}
                  className={`px-6 py-3 rounded-lg font-medium transition-colors duration-300 ${
                    darkMode
                      ? 'bg-purple-700 hover:bg-purple-600 text-white'
                      : 'bg-purple-600 hover:bg-purple-700 text-white'
                  }`}
                >
                  {t('retry')}
                </button>
              </div>
            ) : (
              <div 
                className={`prose prose-lg max-w-none ${
                  darkMode ? 'prose-invert' : ''
                }`}
                dangerouslySetInnerHTML={{ __html: renderMarkdown(content) }}
                style={{
                  lineHeight: '1.7',
                  color: darkMode ? '#e5e7eb' : '#374151'
                }}
              />
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className={`mt-16 py-8 border-t ${
        darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'
      } transition-colors duration-300`}>
        <div className="container mx-auto px-4 text-center">
          <p className={`text-sm ${
            darkMode ? 'text-gray-400' : 'text-gray-600'
          }`}>
            {t('aboutPageFooter')}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;